import {isPlatformBrowser} from '@angular/common';
import {Component, inject, Input, PLATFORM_ID, TemplateRef, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Configurator, OccConfigurator} from '@spartacus/product-configurator/rulebased';
import {SupportFormService} from '../../services';
import {optionalServiceProviderTokens, serviceProviderTokens} from '../../token/tokens';
import ValidationButton = OccConfigurator.ValidationButton;

@UntilDestroy()
@Component({
  selector: 'dcp-configurator-header-messages',
  templateUrl: './dcp-configurator-header-messages.component.html',
  standalone: false,
})
export class DcpConfiguratorHeaderMessagesComponent {
  public platformId = inject(PLATFORM_ID);
  private modalService = inject(serviceProviderTokens.DEFAULT_MODAL_SERVICE_PROVIDER);
  private tokenSupportFormService = inject(optionalServiceProviderTokens.SUPPORT_FORM_SERVICE_PROVIDER, {
    optional: true,
  });

  @Input() configuration!: Configurator.Configuration;
  @Input() externalValidations: any;
  @ViewChild('modalContentTemplate') modalContentTemplate!: TemplateRef<any>;
  modalTitleKey: string = 'productDetails.termsAndConditions';
  modalId: string = '';

  stripeMessageDesign: {[key: string]: string} = {
    warning: 'Warning',
    error: 'Negative',
    info: 'Information',
  };

  constructor(
    protected router: Router,
    protected supportFormService: SupportFormService,
  ) {}

  /**
   * navigates to the passed {@link url}
   *
   * @param url
   */
  navigateByUrl(url: string) {
    if (isPlatformBrowser(this.platformId)) {
      if (url.startsWith(window.location.origin)) {
        this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
          this.router.navigateByUrl(url.replace(window.location.origin, ''));
        });
      } else {
        window.open(url);
      }
    }
  }

  /**
   * opens the associated modal dialog
   */
  openModal(title: string) {
    this.modalId = this.modalService.openModal(this.modalContentTemplate, title);
  }

  closeModal() {
    this.modalService.closeModal(this.modalId);
  }

  /**
   * handles the button action based on the button params.
   * if the passed button {@link ValidationButton} has a filled {@link ValidationButton.url} property, it navigates to the url using {@link navigateByUrl}
   * but if the url is empty, the button target is modal, and it has a {@link ValidationButton.content.body} then a modal is open using {@link DcpConfiguratorHeaderMessagesComponent.openModal}
   *
   * @param button: the button to handle its action
   */
  handleAction(button: ValidationButton) {
    const target = button.target || 'blank';
    if (button.url) {
      this.navigateByUrl(button.url);
    } else if (target === 'modal' && button.content?.body) {
      this.openModal(button.content?.headline || '');
    }
  }

  openSupportForm($event: any) {
    if ($event.target?.href?.includes('/supportrequest')) {
      $event.preventDefault();
      if (this.tokenSupportFormService) {
        this.tokenSupportFormService.openSupportFormFromLink($event.target.search);
      } else {
        this.supportFormService.openSupportFormFromLink($event.target.search);
      }
    }
  }

  getFormattedSeverity(severity: string): string | undefined {
    const formattedSeverity = this.stripeMessageDesign[severity.toLowerCase()];
    return formattedSeverity ? formattedSeverity : undefined;
  }

  displayMessages(): boolean {
    return (this.configuration?.validation && this.configuration.validation.length > 0) || this.externalValidations;
  }
}
